/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";
// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Grid, Switch } from "@mui/material";
import { styled } from "@mui/system";
import Icon from "@mui/material/Icon";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

// Soft UI Dashboard PRO React example components
import DataTable from "Tables/WizardCompetitionsTable";
import SoftTypography from "components/SoftTypography";

// Data
//import dataTableData from "views/competitions-table/data/dataTableData";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import { useSoftUIController } from "context";
import SoftSelect from "components/SoftSelect";
import SoftBox from "components/SoftBox";

const globalCss = require("assets/theme/base/globals");

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

let oddSettingsOptions = [];
let marketSettingsOptions = [];

let oddSettingsEditCached = false;

let currentSportCompetitionsData = [];

/**
 *
 * @param {*} param0
 * @returns
 */
function CompetitionsTable({
  provider,
  template,
  selectedSport,
  filterSportData,
  filterCompetitionData,
  handleStepperButtonStatus,
  handleStepData,
  advancedView,
  filters,
  showCompetitionPromosSwitch,
}) {
  const css = globalCss ? globalCss.default : null;
  const customColors = css.customColors;
  let location = useLocation();
  let href = window.location.href;
  let filter =
    href.indexOf("filters/") > -1
      ? location.state && location.state.stateFilter
      : href.split("edit-filter/")[1];
  let templateId =
    href.indexOf("templates") > -1 ? href.split("templates/")[1].split("/")[0] : template;

  const [controller, dispatch] = useSoftUIController();
  const { providers, platform, permissions } = controller;

  const getHiddenColumns = (isAdvancedView = false) => {
    const hiddenColumnsToSet = ["id", "selected", "sport", "both", "conflictCl"];

    if (!showCompetitionPromosSwitch) {
      hiddenColumnsToSet.push("promos");
    }

    if (!isAdvancedView) {
      hiddenColumnsToSet.push("markets_setting_id", "odds_setting_inplay", "odds_setting_pregame");
    }

    return hiddenColumnsToSet;
  };

  const [higherRole, setHigherRole] = useState(null);
  const [sportsSelected, setSportsSelected] = useState([]);
  const [sport, setSport] = useState("");
  const [competition, setCompetition] = useState("");
  const [toggling, setToggling] = useState(false);
  const [sportType, setSportType] = useState("");
  const [oddSettingsAvailable, setSettingsAvailable] = useState([]);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [multiPregame, setMultiPregame] = useState(false);
  const [multiInplay, setMultiInplay] = useState(false);
  const [multiInherit, setMultiInherit] = useState(false);
  const [multiPromos, setMultiPromos] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(getHiddenColumns(advancedView));
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "id", accessor: "id" },
      { Header: "selected", accessor: "selected" },
      { Header: "sport", accessor: "sport", width: "220" },
      { Header: "region", accessor: "region", width: "220", Cell: renderItem },
      { Header: "competition", accessor: "competition", width: "220", Cell: showConflict },
      {
        Header: "promos",
        accessor: "promos",
        Cell: makeSwitch,
        width: "110px",
        sortType: (rowA, rowB) => {
          if (rowA.original.promos > rowB.original.promos) return -1;
          if (rowB.original.promos > rowA.original.promos) return 1;
        },
      },
      {
        Header: "pregame",
        accessor: "pregame",
        Cell: makeSwitch,
        width: "125px",
        sortType: (rowA, rowB) => {
          if (rowA.original.pregame > rowB.original.pregame) return -1;
          if (rowB.original.pregame > rowA.original.pregame) return 1;
        },
      },
      {
        Header: "inplay",
        accessor: "inplay",
        Cell: makeSwitch,
        width: "110px",
        sortType: (rowA, rowB) => {
          if (rowA.original.inplay > rowB.original.inplay) return -1;
          if (rowB.original.inplay > rowA.original.inplay) return 1;
        },
      },
      {
        Header: "both",
        accessor: "both",
        Cell: makeSwitch,
        width: "80px",
        sortType: (rowA, rowB) => {
          if (rowA.original.both > rowB.original.both) return -1;
          if (rowB.original.both > rowA.original.both) return 1;
        },
      },
      {
        Header: "inherit",
        accessor: "inherit",
        Cell: makeSwitch,
        width: "110px",
        sortType: (rowA, rowB) => {
          if (rowA.original.inherit > rowB.original.inherit) return -1;
          if (rowB.original.inherit > rowA.original.inherit) return 1;
        },
      },
      { Header: "conflict", accessor: "conflictCl", width: "220" },
      {
        Header: "Markets Setting",
        accessor: "markets_setting_id",
        width: "160px",
        Cell: formatMarketSetting,
      },
      {
        Header: "Odds Setting inplay",
        accessor: "odds_setting_inplay",
        width: "160px",
        Cell: formatInplayOddSetting,
      },
      {
        Header: "Odds Setting pregame",
        accessor: "odds_setting_pregame",
        width: "160px",
        Cell: formatPregameOddSetting,
      },
    ],

    rows: filterCompetitionData
      ? filterCompetitionData.map((competition) => {
          const competitionUniId = competition.uni_id ?? competition.uniId;
          return {
            ...competition,
            uni_id: competitionUniId,
            promos: !!competition.promos,
          };
        })
      : [],
  });

  /**
   *
   * @param {*} id
   * @param {*} sport
   * @param {*} region
   * @param {*} competition
   * @param {*} pregame
   * @param {*} inplay
   * @param {*} both
   * @returns
   */
  function createCompetition(
    id: number,
    selected: boolean,
    sport: string,
    region: string,
    competition: string,
    pregame: boolean,
    inplay: boolean,
    both: boolean,
    inherit: boolean,
    conflict: object,
    conflictCl: string,
    blocked: boolean,
    markets_setting_id: number,
    odds_setting_inplay: number,
    odds_setting_pregame: number,
    promos: boolean,
    uni_id
  ) {
    if (filter) {
      //if (inplay != pregame) console.log("Competition dif: ", competition);
      both = false;
      if (inplay && pregame) both = true;
      // if (both) {
      //   inplay = true;
      //   pregame = true;
      // }
    }
    return {
      id,
      selected,
      sport,
      region,
      competition,
      pregame,
      inplay,
      both,
      inherit,
      conflict,
      conflictCl,
      blocked,
      markets_setting_id,
      odds_setting_inplay,
      odds_setting_pregame,
      promos: !!promos,
      uni_id,
    };
  }

  function renderItem({ value, ...rest }) {
    return (
      <SoftTypography
        style={{ fontSize: "1rem" }}
        className={rest.row.original.blocked == true ? "blocked-item" : ""}
      >
        {value}
      </SoftTypography>
    );
  }

  function showConflict({ value, ...rest }) {
    let conflict = rest.row.original.conflict;
    let sport = filterSportData.find((sport) => sport.name == rest.row.original.sport);
    let conflictShow = false;

    if (sport.defaultvalue == true && rest.row.original.inherit == true) {
      conflictShow = true;
    } else {
      if (sport.defaultvalue == false) {
        if (rest.row.original.inherit == false) {
          if (rest.row.original.inplay || rest.row.original.pregame) {
            conflictShow = true;
          }
        }
      } else {
        if (rest.row.original.inplay || rest.row.original.pregame) {
          conflictShow = true;
        }
      }
    }

    if (rest.row.original.blocked) conflictShow = true;

    return conflict && conflictShow ? (
      <HtmlTooltip
        placement={"top"}
        maxwidth={500}
        title={
          <React.Fragment>
            <SoftTypography variant="body2" color="light">
              {rest.row.original.blocked
                ? "Competition Blocked, fed from the Master Feed Provider:"
                : "There are Conclicts with the Following Competitions:"}
            </SoftTypography>
            <ul style={{ marginLeft: "20px", marginRight: "10px" }}>
              {conflict.map((item) => {
                let provider = providers.find((so) => so.value == item.provider_id);
                return (
                  <li key={item.competition_id}>
                    <div style={{ display: "flex" }}>
                      <SoftTypography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: "bold",
                          marginRight: "10px",
                          color: customColors[provider.label],
                        }}
                      >
                        {provider ? provider.label : "-"}
                      </SoftTypography>
                      <div style={{ marginRight: "10px" }}>
                        <span style={{ color: "text" }}>id:</span> {item.competition_id}
                      </div>
                      <div style={{ marginRight: "10px" }}>
                        <span style={{ color: "text" }}>name:</span> {item.competition}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </React.Fragment>
        }
      >
        <SoftTypography
          style={{ fontSize: "1rem" }}
          className={rest.row.original.blocked ? "blocked-item" : ""}
        >
          {value}
          <Icon
            style={{
              display: conflict ? "" : "none",
              color: "#cb0c9f",
              lineHeight: "1em",
              marginLeft: "3px",
              opacity: ".8",
              cursor: "help",
            }}
          >
            warning_amber_icon{" "}
          </Icon>
        </SoftTypography>
      </HtmlTooltip>
    ) : (
      <SoftTypography
        style={{ fontSize: "1rem" }}
        title={higherRole < 49 ? rest.row.original.id : ""}
      >
        {value}
      </SoftTypography>
    );
  }

  function formatInplayOddSetting({ value, ...rest }) {
    return (
      <SoftSelect
        className={
          oddSettingsEditCached &&
          (rest.row.original.blocked == "inplay" || rest.row.original.blocked)
            ? "odd-setting-menu select-disabled"
            : "odd-setting-menu"
        }
        menuShouldScrollIntoView={false}
        options={oddSettingsOptions}
        customSmall={true}
        size={"small"}
        isDisabled={
          !oddSettingsEditCached ||
          rest.row.original.blocked == "inplay" ||
          rest.row.original.blocked
        }
        value={
          oddSettingsOptions
            ? oddSettingsOptions.find((item) => item.value === rest.row.original[rest.column.id])
            : null
        }
        onChange={(item) => rowOddSettingsChange(item, rest.row.original, rest)}
      ></SoftSelect>
    );
  }

  function formatPregameOddSetting({ value, ...rest }) {
    return (
      <SoftSelect
        id={rest.column.id}
        className={
          oddSettingsEditCached &&
          (rest.row.original.blocked == "pregame" || rest.row.original.blocked)
            ? "odd-setting-menu select-disabled"
            : "odd-setting-menu"
        }
        menuShouldScrollIntoView={false}
        options={oddSettingsOptions}
        customSmall={true}
        size={"small"}
        isDisabled={
          !oddSettingsEditCached ||
          rest.row.original.blocked == "pregame" ||
          rest.row.original.blocked
        }
        value={
          rest.row.original[rest.column.id]
            ? oddSettingsOptions
              ? oddSettingsOptions.find((item) => item.value === rest.row.original[rest.column.id])
              : null
            : null
        }
        onChange={(item) => rowOddSettingsChange(item, rest.row.original, rest)}
      ></SoftSelect>
    );
  }

  function formatMarketSetting({ value, ...rest }) {
    return (
      <SoftSelect
        className={
          oddSettingsEditCached && rest.row.original.blocked != true
            ? "odd-setting-menu"
            : "odd-setting-menu select-disabled"
        }
        menuShouldScrollIntoView={false}
        options={marketSettingsOptions}
        customSmall={true}
        size={"small"}
        value={
          marketSettingsOptions
            ? marketSettingsOptions.find((item) => item.value === rest.row.original[rest.column.id])
            : null
        }
        isDisabled={!oddSettingsEditCached || rest.row.original.blocked == true}
        onChange={(item) => rowMarketSettingsChange(item, rest.row.original, rest)}
      ></SoftSelect>
    );
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeSwitch({ value, ...rest }) {
    let disabled = false;
    let sport = filterSportData.find((sport) => sport.name == rest.row.original.sport);

    if (rest.column.Header == "promos") {
      disabled = !(sport && sport.promos) || !rest.row.original.uni_id;
    } else if (!sport || !sport.both) {
      disabled = sport ? !sport[rest.column.Header] : true;
      let competitionBoth = rest.row.original.both;
      if (
        rest.column.Header == "inplay" ||
        rest.column.Header == "pregame" ||
        rest.column.Header == "both"
      ) {
        value === disabled ? false : value;
      }
    }
    if (rest.row.original.inherit && rest.column.Header != "promos") disabled = true;

    if (rest.row.original.blocked == true) {
      disabled = true;
      value = false;
    }

    if (
      rest.column.Header == "inplay" &&
      (rest.row.original.blocked == "inplay" || rest.row.original.blocked)
    ) {
      value === disabled ? false : value;
    }

    if (
      rest.column.Header == "pregame" &&
      (rest.row.original.blocked == "pregame" || rest.row.original.blocked)
    ) {
      value === disabled ? false : value;
    }

    switch (rest.column.id) {
      case "both":
        return (
          <Switch
            className={disabled ? "show-disable-pointer" : ""}
            disabled={!rest.row.original.blocked}
            checked={value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
            both={value ? "enabled" : "disabled"}
          />
        );
      case "inplay":
        return (
          <Switch
            className={disabled ? "show-disable-pointer" : ""}
            disabled={
              rest.row.original.blocked == "inplay" || rest.row.original.blocked == true || disabled
            }
            checked={rest.row.original.blocked == "inplay" || disabled ? false : value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
            inplay={value ? "enabled" : "disabled"}
          />
        );
      case "pregame":
        return (
          <Switch
            className={disabled ? "show-disable-pointer" : ""}
            disabled={
              rest.row.original.blocked == "pregame" ||
              rest.row.original.blocked == true ||
              disabled
            }
            checked={rest.row.original.blocked == "pregame" || disabled ? false : value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
            pregame={value ? "enabled" : "disabled"}
          />
        );
      case "inherit":
        return (
          <Switch
            className={
              rest.row.original.blocked
                ? "show-disable-pointer-blocked"
                : disabled
                ? "show-disable-pointer"
                : ""
            }
            disabled={rest.row.original.blocked}
            checked={value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
            inherit={value ? "enabled" : "disabled"}
          />
        );
      case "promos":
        const promoSwitch = (
          <Switch
            className={disabled ? "show-disable-pointer" : ""}
            disabled={rest.row.original.blocked == true || disabled}
            checked={rest.row.original.blocked == true || !rest.row.original.uni_id ? false : value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
            promos={value ? "enabled" : "disabled"}
          />
        );

        if (!rest.row.original.uni_id) {
          return (
            <HtmlTooltip
              placement={"top"}
              title={
                <React.Fragment>
                  <SoftTypography variant="body2" color="light">
                    Promo Markets are not available in this league
                  </SoftTypography>
                </React.Fragment>
              }
            >
              <span style={{ display: "inline-flex" }}>{promoSwitch}</span>
            </HtmlTooltip>
          );
        }

        return promoSwitch;
      default:
        return (
          <Switch
            className={disabled ? "show-disable-pointer" : ""}
            disabled={disabled}
            checked={value}
            onClick={(event) =>
              handleTypeChange(event, rest.column.id, value, rest.row.original.id)
            }
            inputProps={{ "aria-label": "controlled" }}
          />
        );
        break;
    }
  }

  /**
   *
   * @param {*} e
   * @param {*} type
   * @param {*} value
   * @param {*} competitionId
   */
  const handleTypeChange = (e, type, value, competitionId) => {
    e.preventDefault();
    //console.log(type, competitionId, value);
    let filteredData = { ...dataTableData };
    let tempCompetitions = [...dataTableData.rows];
    const eventTypeSwitches = ["inplay", "pregame", "both"];

    for (const key in tempCompetitions) {
      if (Object.hasOwnProperty.call(tempCompetitions, key)) {
        const competition = tempCompetitions[key];
        if (competition.id === competitionId) {
          let prevTypeValue = competition[type];

          if (eventTypeSwitches.indexOf(type) > -1) {
            competition.both = false;
            if (type === "both") {
            } else if (type === "inplay") {
              competition.inplay = false;
            } else if (type === "pregame") {
              competition.pregame = false;
            }
          }

          competition[type] = !prevTypeValue;
          setSport(competition.sport);
          setCompetition(competition.competition);
          break;
        }
      }
    }

    filteredData.rows = tempCompetitions;
    setDataTableData(filteredData);
    setToggling(!value);
    setSportType(type);
    informWizard(tempCompetitions);
  };

  /**
   *
   * @param {*} e
   * @param {*} type
   * @param {*} value
   * @param {*} competitionId
   */
  const handleMultiTypeChange = (e, type) => {
    e.preventDefault();
    //console.log(type, competitionId, value);
    let filteredData = { ...dataTableData };
    let tempCompetitions = [...dataTableData.rows];
    let value = e.target.value == "on" ? true : false;

    for (const key in tempCompetitions) {
      if (Object.hasOwnProperty.call(tempCompetitions, key)) {
        const competition = tempCompetitions[key];
        if (competition.selected) {
          if (type === "promos") {
            if (competition.blocked == true) continue;

            const sport = filterSportData.find((sport) => sport.name == competition.sport);
            const competitionUniId = competition.uni_id ?? competition.uniId;

            if (!(sport && sport.promos) || !competitionUniId) continue;

            competition.promos = !multiPromos;
            setSport(competition.sport);
            setCompetition(competition.competition);
            competition.modified = true;
            continue;
          }

          let prevTypeValue = competition[type];
          competition.inherit = false;
          if (type === "inherit") {
            competition.inherit = !multiInherit;
          } else if (type === "inplay") {
            competition.inplay = !multiInplay;
          } else if (type === "pregame") {
            competition.pregame = !multiPregame;
          }

          //console.log("competition.blocked: ", type, " - ", competition.blocked);
          if (competition.blocked == true) continue;
          if (type == competition.blocked) continue;
          if (type == "inherit" && competition.blocked) continue;
          //console.log("passed through");
          // competition[type] = !prevTypeValue;
          setSport(competition.sport);
          setCompetition(competition.competition);
          competition.modified = true;
        }
      }
    }

    //console.log("MultiSwitch: ", type, " - ", value);

    if (type == "inplay") {
      setMultiInplay(!multiInplay);
      setMultiInherit(false);
    }
    if (type == "pregame") {
      setMultiPregame(!multiPregame);
      setMultiInherit(false);
    }
    if (type == "inherit") {
      setMultiInherit(!multiInherit);
      setMultiInplay(false);
      setMultiPregame(false);
    }
    if (type == "promos") {
      setMultiPromos(!multiPromos);
    }

    filteredData.rows = tempCompetitions;
    setDataTableData(filteredData);
    setToggling(!value);
    setSportType(type);
    informWizard(tempCompetitions);
  };

  async function getAvailableCompetitions() {
    //console.log("getAvailableCompetitions for sports", filterSportData);
    let sportsIds = filterSportData.map((sport) => {
      return sport.id;
    });

    let getCompetitionsEndpoint = `/api/v1/templates/${templateId}/${provider.id}/available-competitions`;

    let response = await postData(
      getCompetitionsEndpoint,
      { sports: sportsIds },
      { Authorization: getAuthorizationHeader() }
    );
    if (response != null) {
      let myCompetitions = response.data.map((el) => {
        return createCompetition(
          el.id,
          false,
          el.sport,
          el.region,
          el.name,
          el.pregame,
          el.inplay,
          false,
          el.inherit,
          el.conflictProviders,
          el.conflictProviders ? "conflicts" : "",
          el.blocked,
          el.markets_setting_id,
          el.odds_setting_inplay,
          el.odds_setting_pregame,
          !!el.promos,
          el.uni_id ?? el.uniId
        );
      });

      informWizard(myCompetitions);
      return myCompetitions;
    } else {
    }
  }

  function rowMarketSettingsChange(item, row, rest) {
    let filteredData = { ...dataTableData };
    let tempSports = [...dataTableData.rows];

    for (const key in tempSports) {
      if (Object.hasOwnProperty.call(tempSports, key)) {
        const sport = tempSports[key];
        if (sport.id === row.id) {
          row[rest.column.id] = item.value;
        }
      }
    }

    let fullDatarow = currentSportCompetitionsData.find((csc) => csc.id == row.id);
    fullDatarow = row;
    filteredData.rows = tempSports;
    setDataTableData(filteredData);
    informWizard(tempSports);
  }

  function rowOddSettingsChange(item, row, rest) {
    let filteredData = { ...dataTableData };
    let tempSports = [...dataTableData.rows];

    for (const key in tempSports) {
      if (Object.hasOwnProperty.call(tempSports, key)) {
        const sport = tempSports[key];
        if (sport.id === row.id) {
          row[rest.column.id] = item.value;
        }
      }
    }

    let fullDatarow = currentSportCompetitionsData.find((csc) => csc.id == row.id);
    fullDatarow = row;
    filteredData.rows = tempSports;
    setDataTableData(filteredData);
    informWizard(tempSports);
  }

  /**
   *
   */
  async function initData() {
    //console.log("competitions-table:initData");
    handleStepperButtonStatus(true);
    if (filterSportData) {
      setSportsSelected(filterSportData);
      //console.log("applySportsEventType: ", JSON.stringify(filterSportData));

      const data = {
        columns: [
          { Header: "id", accessor: "id" },
          { Header: "selected", accessor: "selected" },
          { Header: "sport", accessor: "sport", width: "220" },
          { Header: "region", accessor: "region", width: "220", Cell: renderItem },
          { Header: "competition", accessor: "competition", width: "220", Cell: showConflict },
          {
            Header: "pregame",
            accessor: "pregame",
            Cell: makeSwitch,
            width: "125px",
            sortType: (rowA, rowB) => {
              if (rowA.original.pregame > rowB.original.pregame) return -1;
              if (rowB.original.pregame > rowA.original.pregame) return 1;
            },
          },
          {
            Header: "inplay",
            accessor: "inplay",
            Cell: makeSwitch,
            width: "110px",
            sortType: (rowA, rowB) => {
              if (rowA.original.inplay > rowB.original.inplay) return -1;
              if (rowB.original.inplay > rowA.original.inplay) return 1;
            },
          },
          {
            Header: "promos",
            accessor: "promos",
            Cell: makeSwitch,
            width: "110px",
            sortType: (rowA, rowB) => {
              if (rowA.original.promos > rowB.original.promos) return -1;
              if (rowB.original.promos > rowA.original.promos) return 1;
            },
          },
          {
            Header: "both",
            accessor: "both",
            Cell: makeSwitch,
            width: "80px",
            sortType: (rowA, rowB) => {
              if (rowA.original.both > rowB.original.both) return -1;
              if (rowB.original.both > rowA.original.both) return 1;
            },
          },
          {
            Header: "inherit",
            accessor: "inherit",
            Cell: makeSwitch,
            width: "110px",
            sortType: (rowA, rowB) => {
              if (rowA.original.inherit > rowB.original.inherit) return -1;
              if (rowB.original.inherit > rowA.original.inherit) return 1;
            },
          },
          { Header: "conflict", accessor: "conflictCl", width: "220" },
          {
            Header: "Markets Setting",
            accessor: "markets_setting_id",
            width: "160px",
            Cell: formatMarketSetting,
          },
          {
            Header: "Odds Setting inplay",
            accessor: "odds_setting_inplay",
            width: "160px",
            Cell: formatInplayOddSetting,
          },
          {
            Header: "Odds Setting pregame",
            accessor: "odds_setting_pregame",
            width: "160px",
            Cell: formatPregameOddSetting,
          },
        ],

        rows: [],
      };

      data.rows = await getAvailableCompetitions();

      let table = dataTableData;
      if (filterCompetitionData && filterCompetitionData.length > 0) {
        table.rows = filterCompetitionData.map((competition) => {
          const competitionUniId = competition.uni_id ?? competition.uniId;
          return {
            ...competition,
            uni_id: competitionUniId,
            promos: !!competition.promos,
          };
        });
      } else {
        let updatedRows = applySportsEventType(data.rows, filterSportData);
        table.rows = updatedRows;
      }

      setDataTableData(table);
      currentSportCompetitionsData = table.rows;
      informWizard(table.rows);
    } else {
      //console.log("without filterSportData");
      const data = { ...dataTableData };
      let updatedRows = applySportsEventType(data.rows, sportsSelected);

      data.rows = updatedRows;
      currentSportCompetitionsData = updatedRows;
      setDataTableData(data);
    }
    handleStepperButtonStatus(false);
  }

  /**
   *
   * @param {*} data
   * @param {*} sportsData
   * @returns
   */
  function applySportsEventType(data, sportsData) {
    let competitions = data;

    if (!filter) {
      for (const key in competitions) {
        if (Object.hasOwnProperty.call(competitions, key)) {
          const competition = competitions[key];
          const competitionSport = competition.sport;
          const currentSportFilter = sportsData.find((sport) => sport.name === competitionSport);
          if (!currentSportFilter) continue;
          if (competition.blocked) continue;

          competition.inplay = currentSportFilter.both ? true : currentSportFilter.inplay;
          competition.pregame = currentSportFilter.both ? true : currentSportFilter.pregame;
          competition.both = currentSportFilter.both;
          competition.promos = currentSportFilter.promos ? !!competition.promos : false;
        }
      }
    } else {
      for (const key in competitions) {
        if (Object.hasOwnProperty.call(competitions, key)) {
          const competition = competitions[key];
          const competitionSport = competition.sport;
          const currentSportFilter = sportsData.find((sport) => sport.name === competitionSport);
          if (!currentSportFilter) continue;
          if (competition.blocked) continue;

          competition.inplay = currentSportFilter.both
            ? competition.inplay
            : currentSportFilter.inplay == false
            ? currentSportFilter.inplay
            : competition.inplay;
          competition.pregame = currentSportFilter.both
            ? competition.pregame
            : currentSportFilter.pregame == false
            ? currentSportFilter.pregame
            : competition.pregame;
          competition.both = currentSportFilter.both;
          competition.promos = currentSportFilter.promos ? !!competition.promos : false;
        }
      }
    }

    return competitions;
  }

  async function fetchOddsSettings() {
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/oddsfilters/platforms/${selectedPlatformId}`;
    let response = await getData(getTemplatesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      oddSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      oddSettingsOptions.unshift({ value: 0, label: "None" });
      setSettingsAvailable(oddSettingsOptions);
    }
  }

  async function fetchMarketsSettings() {
    // if (oddSettingsOptions.length > 0) return;
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}`;
    let response = await getData(getTemplatesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      marketSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      marketSettingsOptions.unshift({ value: 0, label: "None" });
      // setMarketSettingsAvailable(marketSettingsOptions);
    }
  }

  function informWizard(competitions) {
    let competitionsToSend = [];

    for (const key in competitions) {
      if (Object.hasOwnProperty.call(competitions, key)) {
        const competition = competitions[key];
        for (const key in filterSportData) {
          if (Object.hasOwnProperty.call(filterSportData, key)) {
            const sport = filterSportData[key];
            if (sport.name === competition.sport) competitionsToSend.push(competition);
          }
        }
      }
    }
    handleStepData(competitionsToSend);
  }

  const handlePermissions = (permissions) => {
    let editOddSettingsEnabled = false;
    let showOddSettingsEnabled = false;

    //console.log(permissions);

    for (let index = 0; index < permissions.length; index++) {
      const permission = permissions[index];
      if (permission == "super") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
      }
      if (permission == "feed-settings-odds-edit") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
        break;
      }
      if (permission == "feed-settings-odds-show") {
        showOddSettingsEnabled = true;
      }
    }

    oddSettingsEditCached = editOddSettingsEnabled;
  };

  function filterCompetitions(filter) {
    const data = { ...dataTableData };
    let allCompetitions = [...currentSportCompetitionsData];
    let filteredCompetitions = [];
    switch (filter) {
      case "blocked":
        filteredCompetitions = allCompetitions.filter((item) => item.blocked);
        break;
      case "all":
        filteredCompetitions = allCompetitions;
        break;
      case "selected":
        filteredCompetitions = allCompetitions.filter((item) => item.selected);
        break;
      case "pregameon":
        filteredCompetitions = allCompetitions.filter((item) => item.pregame == true);
        break;
      case "inplayon":
        filteredCompetitions = allCompetitions.filter((item) => item.inplay == true);
        break;
      case "marketsetting":
        filteredCompetitions = allCompetitions.filter((item) => item.markets_setting_id);
        break;
      case "inplaysetting":
        filteredCompetitions = allCompetitions.filter((item) => item.odds_setting_inplay);
        break;
      case "pregamesetting":
        filteredCompetitions = allCompetitions.filter((item) => item.odds_setting_pregame);
        break;
      default:
        break;
    }

    data.rows = filteredCompetitions;
    setDataTableData(data);
    //informWizard(allCompetitions);
  }

  const handleTableDataChanges = async ({ key, value, extra }) => {
    //console.log(key, ":", value, extra);
    let currentData = { ...dataTableData };
    if (key == "refresh") {
      initData();
    }

    if (key == "selectOne") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });

      let dataRow = currentData.rows.find((item) => item.id == value);
      dataRow.selected = extra;
      let rows = selectedRows;
      let rowExist = rows.findIndex((item) => item.id == value);
      if (rowExist > -1) {
        if (!extra) {
          rows.splice(rowExist, 1);
          setSelectedRows(rows);
        }
      } else {
        if (extra) {
          rows.push(dataRow);
          setSelectedRows(rows);
        }
      }
      //if (marketsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(getHiddenColumns(advancedView));
      setDataTableData(currentData);
    }

    if (key == "selectAll") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });
      let dataRows = currentData.rows;
      if (extra == "all") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          if (dataRow.blocked == true) continue;
          dataRow.selected = true;
        }
        setSelectedRows(dataRows);
      } else if (extra == "none") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = false;
        }
        setSelectedRows([]);
      }
      //if (marketsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(getHiddenColumns(advancedView));
      setDataTableData(currentData);
    }
  };

  function marketSettingsChange(item, type) {
    for (let index = 0; index < selectedRows.length; index++) {
      const filter = selectedRows[index];
      filter[`markets_setting_id`] = item.value;
      filter.modified = true;
      handleSignals(
        new Event("MarketSettingsChange"),
        filter.id,
        { original: filter },
        filter.filtered
      );
    }
  }

  function oddSettingsChange(item, type) {
    for (let index = 0; index < selectedRows.length; index++) {
      const filter = selectedRows[index];
      if (filter.blocked == type) continue;
      filter[`odds_setting_${type}`] = item.value;
      filter.modified = true;
      handleSignals(
        new Event("OddSettingsChange"),
        filter.id,
        { original: filter },
        filter.filtered
      );
    }
  }

  async function handleSignals(e, id, row, filtered) {
    e.preventDefault();
    let currentData = { ...dataTableData };
    //console.log(row);
    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });

    let dataRow = currentData.rows.find((item) => item.id == row.original.id);
    if (e.type != "OddSettingsChange" && e.type != "MarketSettingsChange") {
      if (dataRow.hasOwnProperty("operFiltered")) {
        dataRow.operFiltered = !dataRow.operFiltered;
        if (!filtered && !dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
        if (filtered && dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
      } else {
        dataRow.operFiltered = filtered ? false : true;
        if (dataRow.operFiltered) dataRow.modified = true;
        else dataRow.modified = false;
      }
    }

    setHiddenColumns(getHiddenColumns(advancedView));
    setDataTableData(currentData);
  }

  useEffect(() => {
    setSport(selectedSport);
  }, [selectedSport]);

  useEffect(() => {
    initData();
    const userData = getUserData();

    let roles = userData.roles.sort((a, b) => (a.id > b.id ? 1 : -1));
    setHigherRole(roles[0].id);
  }, [filterSportData]);

  useEffect(() => {
    if (platform) {
      fetchMarketsSettings();
      fetchOddsSettings();
    }
  }, [platform]);

  useEffect(() => {
    if (permissions.length > 0) handlePermissions(permissions);
  }, [permissions]);

  useEffect(() => {
    setHiddenColumns(getHiddenColumns(advancedView));
    filterCompetitions(filters);
  }, [advancedView, showCompetitionPromosSwitch]);

  useEffect(() => {
    if (filters) filterCompetitions(filters);
  }, [filters]);

  return (
    <>
      <SoftBox
        pt={2}
        pl={3}
        pr={3}
        sx={{
          display: oddSettingsEditCached ? (selectedRows.length > 0 ? "" : "none") : "none",
          position: "relative",
          // zIndex: "1000000",
          top: "0px",
          marginLeft: "auto",
          marginRight: "auto",
          right: "0px",
        }}
        className="table-competition-multiselected-action-filter"
      >
        <Grid
          container
          sx={{
            backgroundImage: "linear-gradient(to right, #FFFFFF, #f8f9fa, #ffffff)",
            minHeight: "90px",
            maxHeight: "130px",
            color: "red !important",
            // textAlign: "right !important",
          }}
        >
          <Grid
            item
            mr={2}
            md={6}
            sm={6}
            sx={{
              textAlign: "right !important",
              width: "200px !important",
              maxWidth: "fit-content !important",
              // paddingLeft: "50px !important",
            }}
          >
            <SoftTypography variant="caption"> </SoftTypography>
            <SoftTypography
              variant="caption"
              style={{ textAlign: "right !important", lineHeight: 10 }}
            >
              Apply to the selected items:{" "}
            </SoftTypography>
          </Grid>
          <Grid item mr={2} style={{ zIndex: 3 }}>
            {showCompetitionPromosSwitch ? (
              <div>
                <SoftTypography variant="caption" style={{ width: "60px" }}>
                  promos:{" "}
                </SoftTypography>
                <Switch
                  className={""}
                  checked={multiPromos}
                  inputProps={{ "aria-label": "controlled" }}
                  onClick={(event) => handleMultiTypeChange(event, "promos")}
                />
              </div>
            ) : null}
            <div>
              <SoftTypography variant="caption" style={{ width: "60px" }}>
                preGame:{" "}
              </SoftTypography>
              <Switch
                className={""}
                checked={multiPregame}
                inputProps={{ "aria-label": "controlled" }}
                onClick={(event) => handleMultiTypeChange(event, "pregame")}
              />
            </div>
            <div>
              <SoftTypography variant="caption" style={{ width: "60px" }}>
                inPlay:{" "}
              </SoftTypography>
              <Switch
                className={""}
                checked={multiInplay}
                inputProps={{ "aria-label": "controlled" }}
                onClick={(event) => handleMultiTypeChange(event, "inplay")}
              />
            </div>
            <div>
              <SoftTypography variant="caption" style={{ width: "60px" }}>
                inherit:{" "}
              </SoftTypography>
              <Switch
                className={""}
                checked={multiInherit}
                inputProps={{ "aria-label": "controlled" }}
                onClick={(event) => handleMultiTypeChange(event, "inherit")}
              />
            </div>
          </Grid>
          <Grid item mr={2} style={{ zIndex: 3, maxWidth: "160px" }}>
            <SoftTypography variant="caption">Markets Settings: </SoftTypography>
            <SoftSelect
              size="small"
              customSmall={true}
              options={marketSettingsOptions}
              onChange={(item) => marketSettingsChange(item, "inplay")}
            ></SoftSelect>
          </Grid>
          <Grid item mr={2} style={{ zIndex: 3, maxWidth: "160px" }}>
            <SoftTypography variant="caption">Odds Settings inPlay: </SoftTypography>
            <SoftSelect
              size="small"
              customSmall={true}
              options={oddSettingsOptions}
              onChange={(item) => oddSettingsChange(item, "inplay")}
            ></SoftSelect>
          </Grid>
          <Grid item mr={2} style={{ zIndex: 3, maxWidth: "160px" }}>
            <SoftTypography variant="caption">Odds Settings preGame: </SoftTypography>
            <SoftSelect
              size="small"
              customSmall={true}
              options={oddSettingsOptions}
              onChange={(item) => oddSettingsChange(item, "pregame")}
            ></SoftSelect>
          </Grid>
        </Grid>
      </SoftBox>
      <DataTable
        table={dataTableData}
        bodyClassName="settlement-fixtures-list"
        hiddenColumns={hiddenColumns}
        selectedsport={sport}
        selectedcompetition={competition}
        type={sportType}
        toggle={toggling}
        canSearch
        checkboxSelection={true}
        informParent={handleTableDataChanges}
      />
    </>
  );
}

// Typechecking props for the DataTable
CompetitionsTable.propTypes = {
  provider: PropTypes.object,
  template: PropTypes.string,
  selectedSport: PropTypes.string,
  filterSportData: PropTypes.array,
  filterCompetitionData: PropTypes.array,
  handleStepperButtonStatus: PropTypes.func,
  handleStepData: PropTypes.func,
  advancedView: PropTypes.bool,
  filters: PropTypes.string,
  showCompetitionPromosSwitch: PropTypes.bool,
};

export default CompetitionsTable;
